import { useState } from "react"
import { createPortal } from "react-dom"
import { LambdaRun, LambdaTrackerUI } from "models/lambda-tracker"
import { formatDateTimeAmPm } from "utils/tools"

interface LambdaRunStripProps {
    runs: LambdaRun[]
    /** Show only the most recent N runs (oldest on the left, newest on the right). */
    max?: number
    /** Wrap onto several lines instead of keeping a single row (detail page). */
    wrap?: boolean
    onSelect: (run: LambdaRun) => void
}

interface HoverState {
    run: LambdaRun
    x: number
    y: number
}

const TOOLTIP_WIDTH = 280

// Site-health style run history: one icon per run, coloured and shaped by its health.
// One shared floating tooltip (instead of one per icon) keeps long histories cheap to render.
const LambdaRunStrip = ({ runs, max, wrap = false, onSelect }: LambdaRunStripProps) => {
    const [hover, setHover] = useState<HoverState | null>(null)
    const visible = max && runs.length > max ? runs.slice(runs.length - max) : runs
    const hidden = runs.length - visible.length

    const show = (run: LambdaRun, el: HTMLElement) => {
        const rect = el.getBoundingClientRect()
        const x = Math.min(Math.max(8, rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2), window.innerWidth - TOOLTIP_WIDTH - 8)
        setHover({ run, x, y: rect.bottom + 6 })
    }

    if (runs.length === 0) {
        return <span className="lambda-run-strip-empty">No runs in this period</span>
    }

    return (
        <div className={`lambda-run-strip ${wrap ? "wrap" : ""}`} onMouseLeave={() => setHover(null)}>
            {hidden > 0 && <span className="lambda-run-strip-more" title={`${hidden} earlier runs not shown`}>+{hidden}</span>}
            {visible.map(run => (
                <button
                    key={run.key}
                    type="button"
                    className={`lambda-run health-${run.health} ${run.status === "Missed" ? "missed" : ""}`}
                    aria-label={`${LambdaTrackerUI.statusLabel(run.status)} ${formatDateTimeAmPm(LambdaTrackerUI.runTime(run))}`}
                    onMouseEnter={e => show(run, e.currentTarget)}
                    onFocus={e => show(run, e.currentTarget)}
                    onBlur={() => setHover(null)}
                    onClick={() => onSelect(run)}
                >
                    <i className={LambdaTrackerUI.icon(run)} aria-hidden />
                </button>
            ))}
            {hover && createPortal(
                <div className="lambda-run-tooltip" style={{ left: hover.x, top: hover.y, width: TOOLTIP_WIDTH }} role="tooltip">
                    <div className="tip-head">
                        <i className={`${LambdaTrackerUI.icon(hover.run)} health-${hover.run.health}`} aria-hidden />
                        <strong>{LambdaTrackerUI.statusLabel(hover.run.status)}</strong>
                        <span className="tip-time">{formatDateTimeAmPm(LambdaTrackerUI.runTime(hover.run))}</span>
                    </div>
                    {hover.run.status !== "Missed" && (
                        <div className="tip-metrics">
                            <span><i className="mdi mdi-timer-outline" aria-hidden /> {LambdaTrackerUI.duration(hover.run.durationMs)}</span>
                            <span><i className="mdi mdi-memory" aria-hidden /> {LambdaTrackerUI.memory(hover.run.maxMemoryUsedMb)}</span>
                            <span><i className="mdi mdi-currency-usd" aria-hidden />{LambdaTrackerUI.cost(hover.run.costUsd).replace("$", "")}</span>
                        </div>
                    )}
                    {hover.run.reason && <div className="tip-reason">{hover.run.reason}</div>}
                    <div className="tip-hint">Click for details</div>
                </div>,
                document.body
            )}
        </div>
    )
}

export default LambdaRunStrip
